package org.teddapp.project.ui.admin

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.padding
import androidx.compose.ui.unit.sp
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.TeddVisibility
import org.teddapp.project.ui.TeddVisibilityBadge

// ──────────────────────────────────────────────────────────────────────
// Réglages du site : où télécharger l'application, et les portraits de la page À propos.
// ──────────────────────────────────────────────────────────────────────

/** Une image choisie sur l'appareil. */
class PickedImage(val bytes: ByteArray, val contentType: String)

/**
 * Sélecteur d'images, fourni par la plateforme qui en a un. Rend l'image choisie, ou null
 * si l'on a annulé. Absent, le back-office dit d'où envoyer la photo.
 */
object TeddImagePicker {
    var pick: (suspend () -> PickedImage?)? = null
    const val MAX_BYTES = 6 * 1024 * 1024

    /** Le type d'une image d'après ses premiers octets, ou null si ce n'est ni JPEG, ni PNG, ni WebP. */
    fun typeOf(bytes: ByteArray): String? = when {
        bytes.size > 3 && bytes[0] == 0xFF.toByte() && bytes[1] == 0xD8.toByte() && bytes[2] == 0xFF.toByte() -> "image/jpeg"
        bytes.size > 4 && bytes[0] == 0x89.toByte() && bytes[1] == 'P'.code.toByte() && bytes[2] == 'N'.code.toByte() && bytes[3] == 'G'.code.toByte() -> "image/png"
        bytes.size > 12 && bytes.decodeToString(0, 4) == "RIFF" && bytes.decodeToString(8, 12) == "WEBP" -> "image/webp"
        else -> null
    }
}

/** Les plateformes proposées sur le site, dans l'ordre où il les montre. */
private val PLATFORMS = listOf(
    "android" to "Android (fichier APK)",
    "ios" to "iPhone et iPad (TestFlight ou App Store)",
    "windows" to "Windows",
    "mac-arm" to "Mac · Apple Silicon",
    "mac-intel" to "Mac · Intel",
    "linux" to "Linux (paquet .deb)",
    "web" to "Application dans le navigateur",
)

private val PHOTOS = listOf(
    1 to ("Portrait principal" to "Dans le grand cadre de la page À propos."),
    2 to ("Seconde photo" to "En médaillon, sur le portrait principal."),
)

/** Une rubrique du site : son en-tête avec son geste de création, le formulaire ouvert, puis sa liste. */
@Composable
internal fun SiteTab(model: AdminModel, compact: Boolean) {
    val gap = if (compact) TeddSpace.S4 else TeddSpace.S5
    val r = model.rubric
    Column(verticalArrangement = Arrangement.spacedBy(gap)) {
        RubricHeader(model, compact) {
            if (model.form == null) when (r) {
                SiteRubric.Portfolio, SiteRubric.Blog -> {
                    val kind = if (r == SiteRubric.Blog) "note" else "portfolio_piece"
                    // Écrire ici, ou publier ce que l'appareil a déjà : une note, une pièce.
                    TeddButton(
                        text = if (r == SiteRubric.Blog) "Depuis une note" else "Depuis une pièce",
                        onClick = { model.openPublish(PublicationForm.Content(kind = kind, lang = "fr", lockKind = true, lockLang = true)) },
                        kind = TeddButtonKind.Ghost,
                        size = if (compact) TeddButtonSize.Sm else TeddButtonSize.Md,
                        icon = "notes",
                        enabled = !model.busy,
                    )
                    TeddButton(
                        text = if (r == SiteRubric.Blog) "Nouvel article" else "Nouveau projet",
                        onClick = { model.newDraft(kind, "fr") },
                        kind = TeddButtonKind.Primary,
                        size = if (compact) TeddButtonSize.Sm else TeddButtonSize.Md,
                        icon = "plus",
                        enabled = !model.busy,
                    )
                }
                SiteRubric.Media -> TeddButton(
                    text = "Ajouter une image",
                    onClick = { model.addToGallery() },
                    kind = TeddButtonKind.Primary,
                    size = if (compact) TeddButtonSize.Sm else TeddButtonSize.Md,
                    icon = "plus",
                    enabled = !model.busy && TeddImagePicker.pick != null,
                )
                SiteRubric.Other -> TeddButton(
                    text = "Nouvelle page libre",
                    onClick = { model.newDraft("page", "fr") },
                    kind = TeddButtonKind.Primary,
                    size = if (compact) TeddButtonSize.Sm else TeddButtonSize.Md,
                    icon = "plus",
                    enabled = !model.busy,
                )
                else -> Unit
            }
        }
        OpenForm(model)
        when (r) {
            SiteRubric.Home -> RolePanel(model, "home", compact)
            SiteRubric.About -> RolePanel(model, "about", compact)
            SiteRubric.Portfolio -> PairedPanel(model, "portfolio_piece", compact)
            SiteRubric.Blog -> PairedPanel(model, "note", compact)
            SiteRubric.Other -> OtherPanel(model, compact)
            SiteRubric.Media -> MediaPanel(model, compact)
            SiteRubric.Settings -> SettingsPanel(model, compact)
            SiteRubric.Accounts -> Unit
        }
    }
}

/** Visites, exemples et téléchargements ; les photos vivent dans Médias. */
@Composable
private fun SettingsPanel(model: AdminModel, compact: Boolean) {
    val site = model.site ?: return
    Column(verticalArrangement = Arrangement.spacedBy(if (compact) TeddSpace.S4 else TeddSpace.S5)) {
        model.stats?.site?.let { VisitsCard(it, compact) }
        Box(Modifier.widthIn(max = 720.dp)) { ExamplesCard(model) }
        Box(Modifier.widthIn(max = 720.dp)) { DownloadsCard(model, site.downloads) }
    }
}

/**
 * Les visites du site, comptées par le serveur : visiteurs distincts et pages vues sur
 * le jour, sept jours et trente jours, puis les pages les plus lues du mois.
 */
@Composable
private fun VisitsCard(v: org.teddapp.project.server.ServerSiteStats, compact: Boolean) {
    TeddCard(modifier = Modifier.fillMaxWidth(), title = "Visites du site", icon = "globe", padding = PaddingValues(TeddSpace.S5)) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
            Text(
                "Visiteurs distincts et pages vues, comptés par le serveur à chaque page ouverte. Sans cookie : un visiteur est une empreinte qui change chaque jour.",
                color = TeddTokens.InkMuted, style = TeddType.Caption,
            )
            val tiles = listOf(
                Triple("AUJOURD'HUI", v.visitors_today, "${v.views_today} page${if (v.views_today > 1) "s" else ""} vue${if (v.views_today > 1) "s" else ""}"),
                Triple("7 JOURS", v.visitors_7d, "${v.views_7d} pages vues"),
                Triple("30 JOURS", v.visitors_30d, "${v.views_30d} pages vues"),
            )
            androidx.compose.foundation.layout.FlowRow(
                horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
                verticalArrangement = Arrangement.spacedBy(TeddSpace.S3),
                maxItemsInEachRow = if (compact) 1 else 3,
            ) {
                tiles.forEach { (label, visitors, views) ->
                    Column(
                        modifier = Modifier.weight(1f).background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd)).padding(TeddSpace.S4),
                        verticalArrangement = Arrangement.spacedBy(2.dp),
                    ) {
                        Text(label, color = TeddTokens.InkMuted, style = TeddType.Overline)
                        Text("$visitors", color = TeddTokens.Ink, style = TeddType.MonoSm.copy(fontSize = 26.sp))
                        Text("visiteur${if (visitors > 1) "s" else ""} · $views", color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1, overflow = TextOverflow.Ellipsis)
                    }
                }
            }
            if (v.top_pages_30d.isEmpty() && v.app_views_30d == 0L && v.downloads_30d.isEmpty()) {
                Text("Aucune visite comptée pour l'instant : le site reçoit la balise à sa prochaine reconstruction.", color = TeddTokens.InkMuted, style = TeddType.Caption)
            } else {
                val columns: @Composable (List<Pair<String, @Composable () -> Unit>>) -> Unit = { blocks ->
                    if (compact) Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) { blocks.forEach { (_, block) -> block() } }
                    else Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S5), verticalAlignment = Alignment.Top) { blocks.forEach { (_, block) -> Box(Modifier.weight(1f)) { block() } } }
                }
                columns(
                    listOf(
                        "pages" to { CountList("PAGES LES PLUS LUES · 30 JOURS", v.top_pages_30d.map { it.path to it.views }, "Aucune page vue.") },
                        "from" to { CountList("D'OÙ ILS VIENNENT · 30 JOURS", v.referrers_30d.map { it.path to it.views }, "Pas encore de provenance.") },
                    ),
                )
                columns(
                    listOf(
                        "dl" to {
                            CountList(
                                "TÉLÉCHARGEMENTS · 30 JOURS",
                                v.downloads_30d.map { (PLATFORMS.firstOrNull { p -> p.first == it.path }?.second ?: it.path) to it.views },
                                "Aucun téléchargement compté : les cartes du site passent par le serveur depuis la 3.19.0.",
                            )
                        },
                        "app" to {
                            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                                Text("L'APPLI DANS LE NAVIGATEUR · 30 JOURS", color = TeddTokens.InkMuted, style = TeddType.Overline)
                                Text(
                                    if (v.app_views_30d == 0L) "Aucune ouverture comptée."
                                    else "${v.app_visitors_30d} visiteur${if (v.app_visitors_30d > 1) "s" else ""} · ${v.app_views_30d} ouverture${if (v.app_views_30d > 1) "s" else ""}",
                                    color = TeddTokens.Ink, style = TeddType.MonoSm,
                                )
                            }
                        },
                    ),
                )
            }
        }
    }
}

/** Une liste de comptes : un libellé, un nombre à droite ; ou une phrase quand elle est vide. */
@Composable
private fun CountList(title: String, rows: List<Pair<String, Long>>, empty: String) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
        Text(title, color = TeddTokens.InkMuted, style = TeddType.Overline)
        if (rows.isEmpty()) Text(empty, color = TeddTokens.InkMuted, style = TeddType.Caption)
        rows.forEach { (label, count) ->
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
                Text(label, color = TeddTokens.Ink, style = TeddType.MonoSm, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                Text("$count", color = TeddTokens.InkMuted, style = TeddType.MonoSm)
            }
        }
    }
}

@Composable
internal fun PhotosCard(model: AdminModel, compact: Boolean) {
    val site = model.site ?: return
    run {
        TeddCard(modifier = Modifier.fillMaxWidth(), title = "Photos de la page À propos", icon = "users", padding = PaddingValues(TeddSpace.S5)) {
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
                Text(
                    "JPEG, PNG ou WebP, 6 Mo au plus, de préférence en portrait. Le site les réduit à la bonne taille.",
                    color = TeddTokens.InkMuted, style = TeddType.Caption,
                )
                if (TeddImagePicker.pick == null) Text(
                    "Cet appareil ne sait pas encore choisir une image : envoie tes photos depuis l'ordinateur, Android ou le navigateur.",
                    color = TeddTokens.InkMuted, style = TeddType.Caption,
                )
                PHOTOS.forEach { (slot, text) ->
                    val photo = site.photos.firstOrNull { it.slot == slot }
                    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                            Text(text.first, color = TeddTokens.Ink, style = TeddType.BodyStrong, modifier = Modifier.weight(1f))
                            if (photo != null) TeddVisibilityBadge(TeddVisibility.Public, label = "En ligne") else TeddTag("Aucune photo")
                        }
                        Text(
                            if (photo != null) "${text.second} ${formatBytes(photo.size_bytes)}, envoyée le ${formatDay(photo.updated_at_ms)}." else text.second,
                            color = TeddTokens.InkMuted, style = TeddType.Caption,
                        )
                        Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                            TeddButton(
                                text = if (photo != null) "Remplacer" else "Choisir une photo",
                                onClick = { model.choosePhoto(slot) },
                                kind = if (photo != null) TeddButtonKind.Secondary else TeddButtonKind.Primary,
                                size = TeddButtonSize.Sm,
                                enabled = !model.busy && TeddImagePicker.pick != null,
                            )
                            if (photo != null) TeddButton(text = "Retirer", onClick = { model.removePhoto(slot) }, size = TeddButtonSize.Sm, kind = TeddButtonKind.Ghost, enabled = !model.busy)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DownloadsCard(model: AdminModel, saved: Map<String, String>) {
    var urls by remember(saved) { mutableStateOf(saved) }
    val changed = urls.filterValues { it.isNotBlank() }.mapValues { it.value.trim() } != saved
    TeddCard(modifier = Modifier.fillMaxWidth(), title = "Téléchargements", icon = "globe", padding = PaddingValues(TeddSpace.S5)) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            Text(
                "L'adresse du fichier ou de la fiche de chaque plateforme. Une plateforme sans adresse s'affiche « Bientôt disponible » sur le site.",
                color = TeddTokens.InkMuted, style = TeddType.Caption,
            )
            PLATFORMS.forEach { (key, label) ->
                TeddField(
                    label = label,
                    value = urls[key].orEmpty(),
                    onValueChange = { value -> urls = urls + (key to value.trim()) },
                    keyboardType = KeyboardType.Uri,
                    placeholder = "https://…",
                )
            }
            FormError(model.formError)
            TeddButton(
                text = if (model.busy) "Enregistrement…" else "Enregistrer",
                onClick = { model.saveDownloads(urls.filterValues { it.isNotBlank() }) },
                kind = TeddButtonKind.Publish,
                enabled = !model.busy && changed,
            )
        }
    }
}
